<template>
  <div class="import-log" :class="{ 'import-log--fill': fill || bare, 'import-log--bare': bare }">
    <div v-if="!bare" class="import-log__head">
      <span class="import-log__title">Import-Log</span>
      <span class="import-log__count">{{ countText }}</span>
    </div>
    <div v-if="state === 'loading'" class="import-log__state" role="status">Wird geladen …</div>
    <div v-else-if="state === 'error'" class="import-log__state import-log__state--error" role="alert">
      Das Log konnte nicht geladen werden.
    </div>
    <div v-else-if="!entries.length" class="import-log__state">Keine Einträge.</div>
    <ol v-else class="import-log__lines" tabindex="0" aria-label="Import-Log">
      <li
        v-for="entry in entries"
        :key="entry.id"
        class="import-log__line"
        :class="{ 'import-log__line--error': isError(entry.message) }"
      >
        <time class="import-log__time" :datetime="entry.creation_time">{{ timeText(entry.creation_time) }}</time>
        <span class="import-log__msg">{{ entry.message }}</span>
      </li>
    </ol>
  </div>
</template>

<script>
// Import-Log einer Fahrplanversion im aufgeklappten Detail der Fahrpläne (Kundenwunsch 13.10.2026: „vielleicht kann
// man es auch so als technisch komprimierten Log anzeigen, anstatt nochmal als Tabelle direkt darunter“): kompakte
// Zeilen in Festbreitenschrift „Zeit  Meldung“, neueste oben, höchstens 40 % der Fensterhöhe mit eigenem Scrollen.
// Meldungen mit „Fehler“, „error“ oder „failed“ in Rot. Daten wie bisher: GET versionimportlog?version_id=…, neue
// Einträge live über den Websocket „version-import-log/{version_id}“. fill: ohne eigene Höchsthöhe (der Detailbereich
// scrollt). bare (Abschnitt im Detail der Fahrplanversion): ohne Kopf, als Block mit Fläche --band, Kante und Radius.
import axios from "axios";
import moment from "moment";
import websocket from "@/aws/websocket";
import { formatNumber, plural } from "@/utils/format";

function byNewest(a, b) {
  return (Number(b.id) || 0) - (Number(a.id) || 0);
}

export default {
  props: {
    version_id: { type: [Number, String], default: null },
    fill: { type: Boolean, default: false },
    bare: { type: Boolean, default: false }
  },
  data() {
    return {
      state: "loading",
      entries: []
    };
  },
  computed: {
    countText() {
      return this.state === "ready"
        ? formatNumber(this.entries.length) + " " + plural(this.entries.length, "Eintrag", "Einträge")
        : "";
    }
  },
  methods: {
    timeText(value) {
      const m = value ? moment(value) : null;
      return m && m.isValid() ? m.format("DD.MM.YYYY HH:mm:ss") : "–";
    },
    isError(message) {
      return /fehler|error|failed/i.test(String(message || ""));
    },
    async load() {
      this.state = "loading";
      try {
        const response = await axios.get(
          `${this.$store.state.baseUrl}versionimportlog?version_id=${this.version_id}`
        );
        this.entries = (Array.isArray(response.data) ? response.data : []).slice().sort(byNewest);
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    }
  },
  async mounted() {
    this.load();
    websocket.subscribe(`version-import-log/${this.version_id}`, message => {
      if (message.data.type === "INSERT") {
        this.entries = [message.data.data].concat(this.entries).sort(byNewest);
      }
    });
  },
  destroyed() {
    websocket.unsubscribe(`version-import-log/${this.version_id}`);
  }
};
</script>

<style scoped>
.import-log {
  font-family: var(--font);
}
.import-log__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 16px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.import-log__title {
  font-weight: 600;
  color: var(--ink-2);
}
.import-log__state {
  padding: 8px 16px 12px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.import-log__state--error {
  color: var(--red);
}
/* technisch und kompakt: Festbreitenschrift, eine Zeile je Meldung, eigenes Scrollen */
.import-log__lines {
  max-height: 40vh;
  overflow-y: auto;
  margin: 0;
  padding: 0 0 8px;
  list-style: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  line-height: 18px;
  color: var(--ink-2);
}
.import-log--fill .import-log__lines {
  max-height: none;
  overflow: visible;
}
/* Block im Detail: Fläche und Kante wie ein Code-Block, Zeilen mit 12 Polster */
.import-log--bare {
  border: 1px solid var(--line-2);
  border-radius: var(--r-ctl);
  background: var(--band);
  overflow: hidden;
}
.import-log--bare .import-log__lines {
  padding: 6px 0;
}
.import-log--bare .import-log__line {
  padding: 1px 12px;
}
.import-log--bare .import-log__state {
  padding: 8px 12px;
}
.import-log__lines:focus-visible {
  outline-offset: -2px;
}
.import-log__line {
  display: flex;
  gap: 16px;
  padding: 1px 16px;
}
:where(html:not(.dichte-touch)) .import-log__line:hover {
  background: var(--hov);
}
.import-log__time {
  flex: none;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
.import-log__msg {
  min-width: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.import-log__line--error .import-log__msg {
  color: var(--red);
}
</style>
